// Read the same product groups as the PDP so separate colour products stay in sync.
const colorGroups = new Map();

function readColorGroup(root, productId) {
  for (const script of root.querySelectorAll('[data-cart-upsell-colors]')) {
    const products = JSON.parse(script.textContent);
    if (products.some((product) => product.id === productId)) return products;
  }
  return null;
}

async function loadColorGroup(product) {
  const existing = readColorGroup(document, product.id);
  if (existing) return existing;
  if (!colorGroups.has(product.url)) {
    const request = fetch(product.url)
      .then((response) => {
        if (!response.ok) throw new Error('Could not load linked colours');
        return response.text();
      })
      .then((html) => readColorGroup(new DOMParser().parseFromString(html, 'text/html'), product.id))
      .catch((error) => {
        colorGroups.delete(product.url);
        throw error;
      });
    colorGroups.set(product.url, request);
  }
  return colorGroups.get(product.url);
}

class CartUpsell extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.events = new AbortController();
    const listenerOptions = { signal: this.events.signal };
    this.product ||= JSON.parse(this.querySelector('[data-upsell-product]').textContent);
    this.products ||= [this.product];
    this.form = this.querySelector('form');
    this.colorSelect = this.querySelector('[data-color]');
    this.variantSelect = this.querySelector('[data-variant]');
    this.addButton = this.querySelector('button[type="submit"]');
    this.error = this.querySelector('.cart-upsell__error');

    this.colorSelect.addEventListener('change', () => {
      const previousTitle = this.selectedVariant?.title;
      this.product = this.products.find((product) => String(product.id) === this.colorSelect.value);
      this.renderVariants(previousTitle);
    }, listenerOptions);
    this.variantSelect.addEventListener('change', () => this.renderSelection(), listenerOptions);
    this.form.addEventListener('cart:error', (event) => {
      this.error.textContent = event.detail.error;
      this.error.hidden = false;
    }, listenerOptions);
    this.form.addEventListener('submit', (event) => {
      if (!this.selectedVariant?.available || this.product.requiresSellingPlan) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
      this.error.hidden = true;
    }, { ...listenerOptions, capture: true });
    // ProductForm owns AJAX submission and the theme's cart refresh events.
    this.busyObserver = new MutationObserver(() => {
      const busy = this.addButton.getAttribute('aria-busy') === 'true';
      this.colorSelect.disabled = busy;
      this.variantSelect.disabled = busy;
    });
    this.busyObserver.observe(this.addButton, { attributes: true, attributeFilter: ['aria-busy'] });
    this.renderSelection();

    this.visibilityObserver = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        this.visibilityObserver.disconnect();
        this.loadColors();
      }
    });
    this.visibilityObserver.observe(this);
  }

  disconnectedCallback() {
    this.visibilityObserver?.disconnect();
    this.busyObserver?.disconnect();
    this.events?.abort();
    this.initialized = false;
    // Cart sections are replaced after every add; listeners belong to that old node.
  }

  get selectedVariant() {
    return this.product.variants.find((variant) => String(variant.id) === this.variantSelect.value);
  }

  async loadColors() {
    try {
      const products = await loadColorGroup(this.product);
      if (!this.isConnected || !products || products.length < 2) return;
      this.products = products;
      this.colorSelect.replaceChildren(...products.map((product) => {
        const available = product.variants.some((variant) => variant.available);
        const option = new Option(`${product.label}${available ? '' : ` — ${this.dataset.soldOut}`}`, String(product.id));
        option.selected = product.id === this.product.id;
        option.disabled = !available;
        return option;
      }));
      this.querySelector('[data-color-field]').hidden = false;
    } catch {
      // The initial product's sizes and Add button remain usable if its PDP is unavailable.
    }
  }

  renderVariants(previousTitle) {
    const variant = this.product.variants.find((item) => item.available && item.title === previousTitle)
      || this.product.variants.find((item) => item.available)
      || this.product.variants[0];
    this.variantSelect.replaceChildren(...this.product.variants.map((item) => {
      const option = new Option(`${item.title}${item.available ? '' : ` — ${this.dataset.soldOut}`}`, String(item.id));
      option.disabled = !item.available;
      option.selected = item.id === variant?.id;
      return option;
    }));
    this.querySelector('[data-size-field]').hidden = this.product.variants.length === 1 && variant?.title === 'Default Title';
    this.renderSelection();
  }

  renderSelection() {
    const variant = this.selectedVariant;
    this.form.elements.namedItem('id').value = variant?.id || '';
    this.addButton.disabled = !variant?.available;
    this.addButton.hidden = Boolean(this.product.requiresSellingPlan);
    this.addButton.textContent = variant?.available ? this.dataset.addLabel : this.dataset.soldOut;
    this.addButton.setAttribute('aria-label', `${this.addButton.textContent} — ${this.product.title}`);
    this.querySelector('[data-plan-link]').hidden = !this.product.requiresSellingPlan;
    this.querySelector('[data-plan-link]').href = this.product.url;
    this.querySelector('[data-price]').textContent = variant?.price || '';
    const comparePrice = this.querySelector('[data-compare-price]');
    comparePrice.textContent = variant?.compareAt || '';
    comparePrice.hidden = !variant?.compareAt;
    this.querySelector('.cart-upsell__title').textContent = this.product.title;
    const url = new URL(this.product.url, window.location.origin);
    if (variant) url.searchParams.set('variant', variant.id);
    this.querySelectorAll('[data-product-link]').forEach((link) => {
      link.href = url.href;
      link.setAttribute('aria-label', this.product.title);
    });
    const media = this.querySelector('.cart-upsell__media');
    const imageUrl = variant?.image || this.product.image;
    let image = media.querySelector('img');
    if (imageUrl) {
      if (!image) {
        image = document.createElement('img');
        image.className = 'cart-upsell__image';
        media.append(image);
      }
      image.removeAttribute('srcset');
      image.src = imageUrl;
      image.alt = this.product.title;
    } else {
      image?.remove();
    }
    this.error.hidden = true;
  }
}

if (!customElements.get('cart-upsell')) customElements.define('cart-upsell', CartUpsell);
